import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../utils/axios/requestInfo";
import { api } from "../../../resources/api";
import { WindowNames } from "../../../resources/constants";
import { AuthDataType, IRespData, RegDataType, testPhone, IFetchCodeArgs } from "./types";
import { setToken, setPhoneData } from "../../auth/slice";
import { setPartnerId } from "../../user/slice";
import { setAuth } from "./slice";
import { bitrixRegister } from "./bitrixRegister";
import { bitrixPartnerLookup } from "./bitrixPartnerLookup";
import { hashToken } from "../../../helpers/hashToken";
import { RootState } from "../../store";
import { addName, addEmail, addPassword } from "../settings/changePass/slice";
import { fetchUserData } from "../../user/asyncActions";
import { getSendCodeErrorMessage } from "../../../helpers/sendCodeError";

export const fetchAuth = createAsyncThunk<IRespData, AuthDataType>(
  "authAndReg/fetchAuth",
  async (data, { dispatch }) => {
    try {
      const response = await axiosInstance.post(api.profile.auth.LOGIN, data);

      if (response.status === 200 || response.status === 201) {
        const responseData = response.data?.data || response.data;
        const { access_token, partner_id } = responseData;
        
        dispatch(setToken(access_token));
        dispatch(setPhoneData(data.phone));
        dispatch(setAuth(access_token));
        if (partner_id) {
          dispatch(setPartnerId(partner_id));
        }
        
        try {
          await dispatch(fetchUserData()).unwrap();
        } catch (userDataError) {
        }
        
        return { showWindow: "", auth: true };
      } else {
        return { showWindow: WindowNames.ERROR, auth: false };
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || "Ошибка авторизации";
      if (error.response?.status === 422) {
        throw new Error(`422:${errorMsg}`);
      }
      throw new Error(errorMsg);
    }
  }
);

export const fetchReg = createAsyncThunk<IRespData, RegDataType>(
  "authAndReg/fetchReg",
  async (data, { dispatch }) => {
    try {
      const hashTokenValue = hashToken(data.values.phone + data.values.email);

      const response = await axiosInstance.post(
        api.profile.registration.REG,
        {
          name: data.values.name,
          email: data.values.email,
          phone: data.values.phone,
          password: data.values.password,
          passwordRepeat: data.values.passwordRepeat,
        },
        {
          headers: {
            Authorization: `Bearer ${hashTokenValue}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        dispatch(setPhoneData(data.values.phone));
        
        dispatch(addName(data.values.name));
        dispatch(addEmail(data.values.email));
        dispatch(addPassword({
          password: data.values.password,
          passwordRepeat: data.values.passwordRepeat,
        }));

        data.actions.setSubmitting(false);
        return { showWindow: WindowNames.CODE, auth: false };
      } else {
        data.actions.setSubmitting(false);
        return { showWindow: WindowNames.ERROR, auth: false };
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        const errors: { [key: string]: string } = {};

        for (let key in error.response.data.errors) {
          errors[key] = error.response.data.errors[key][0];
        }
        data.actions.setErrors(errors);
        data.actions.setSubmitting(false);
        const errorMsg = Object.values(errors).join(", ") || "Ошибка регистрации";
        throw new Error(errorMsg);
      } else {
        data.actions.setSubmitting(false);
        const serverMessage: string = error.response?.data?.message || "";
        let errorMsg: string;
        if (serverMessage.includes("already registered")) {
          errorMsg = "Пользователь с таким номером уже существует";
        } else if (serverMessage.includes("Not allowed")) {
          // Форма сама подменяет его на «Сервер временно недоступен»
          errorMsg = serverMessage;
        } else {
          // signup — это и есть отправка кода: любой другой сбой = код не ушёл
          errorMsg = getSendCodeErrorMessage(serverMessage);
        }
        throw new Error(errorMsg);
      }
    }
  }
);

export const fetchRequestCode = createAsyncThunk<IRespData, testPhone>(
  "reg/fetchReqCode",
  async (data) => {
    try {
      const response = await axiosInstance.post(
        api.profile.settings.changePass.REQUEST_CODE,
        data
      );
      if (response.status === 200) {
        return { showWindow: WindowNames.REGISTRATION_SUCCESS, auth: true };
      } else {
        return { showWindow: WindowNames.ERROR, auth: false };
      }
    } catch (error: any) {
      return error.response?.status === 422
        ? { showWindow: WindowNames.AUTH_ERROR, auth: false }
        : { showWindow: WindowNames.ERROR, auth: false };
    }
  }
);

export const fetchApplyCode = createAsyncThunk<IRespData, IFetchCodeArgs>(
  "changePass/fetchSendCode",
  async (data, { dispatch, getState }) => {
    try {
      const response = await axiosInstance.post(
        api.profile.auth.CONFIRM,
        {
          phone: data.phone,
          code: data.code,
        }
      );
      
      if (response.status === 200 || response.status === 201) {
        const responseData = response.data?.data || response.data;
        const { access_token, partner_id } = responseData;
        
        dispatch(setToken(access_token));
        dispatch(setPhoneData(data.phone));
        dispatch(setAuth(access_token));
        
        const state = getState() as RootState;
        const registrationData = state.changePass;
        const { name, email } = registrationData;
        
        let partnerId: string | number | null = partner_id || null;
        
        if (!partnerId && name && email) {
          try {
            partnerId = await bitrixPartnerLookup({
              phone: data.phone,
              skipPartnerIdCheck: true,
            });
            
            if (!partnerId) {
              partnerId = await bitrixRegister(
                data.phone,
                name,
                email,
                true
              );
            }
            
            if (partnerId) {
              dispatch(setPartnerId(partnerId));
            }
            
            if (partnerId) {
              await axiosInstance.put(
                api.profile.settings.getProfile.GET_PROFILE,
                {
                  name: name,
                  phone: data.phone,
                  email: email,
                  partner_id: partnerId,
                }
              );
            }
          } catch (bitrixError) {
          }
        } else if (partnerId) {
          dispatch(setPartnerId(partnerId));
        }
        
        try {
          await dispatch(fetchUserData()).unwrap();
        } catch (userDataError) {
        }
        
        return { showWindow: WindowNames.REGISTRATION_SUCCESS, auth: true };
      } else {
        return { showWindow: WindowNames.ERROR, auth: false };
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || "Ошибка подтверждения кода";
      if (error.response?.status === 422) {
        throw new Error(`422:${errorMsg}`);
      }
      throw new Error(errorMsg);
    }
  }
);
